import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { api } from "../lib/api";
import { computeQuote, seedMilestonesFor } from "../projects/pricing";

const requestsCol = collection(db, "requests");

export const subscribeAllRequests = (cb) =>
  onSnapshot(query(requestsCol, orderBy("createdAt", "desc")), (snap) =>
    cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  );

// Every request belonging to an account — the client dashboard's request feed.
export const subscribeAccountRequests = (accountId, cb) => {
  if (!accountId) {
    cb([]);
    return () => {};
  }
  return onSnapshot(
    query(requestsCol, where("accountId", "==", accountId), orderBy("createdAt", "desc")),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  );
};

// A client submits a request for their account. Rules require the request to
// carry the caller's own accountId and createdByUid.
export const createRequest = async ({
  accountId,
  createdByUid,
  type,
  scope,
  notes,
  timeline,
  paymentPlan,
}) => {
  if (!accountId) throw new Error("accountId required");
  const { budgetUsd } = computeQuote({ type, timeline, scope });
  const ref = await addDoc(requestsCol, {
    accountId,
    createdByUid: createdByUid || null,
    type,
    scope: scope || [],
    notes: notes || "",
    timeline,
    paymentPlan,
    budgetUsd,
    status: "submitted",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  // Let the studio know (fire-and-forget).
  api.post("/customer-event", { event: "request_submitted", detail: type }).catch(() => {});
  return ref;
};

export const setRequestStatus = (id, status) =>
  updateDoc(doc(db, "requests", id), { status, updatedAt: serverTimestamp() });

// Atomic accept: create the project under the request's ACCOUNT + seed
// milestones + flag the request accepted. Membership/isClient is owned by the
// accounts API, so this no longer touches user docs.
export const acceptRequest = async ({ requestId, title, summary }) => {
  const reqRef = doc(db, "requests", requestId);
  const reqSnap = await getDoc(reqRef);
  if (!reqSnap.exists()) throw new Error("Request not found");
  const req = reqSnap.data();
  if (!req.accountId) throw new Error("Request has no account — reassign it to an account first");

  const projectRef = doc(collection(db, "projects"));
  const milestones = seedMilestonesFor({
    paymentPlan: req.paymentPlan,
    budgetUsd: req.budgetUsd,
  });

  const batch = writeBatch(db);

  batch.set(projectRef, {
    accountId: req.accountId,
    primaryContactUid: req.createdByUid || null,
    requestId,
    title: title || `Project for ${req.accountId.slice(0, 6)}`,
    summary: summary || req.notes || "",
    type: req.type,
    status: "discovery",
    budgetUsd: req.budgetUsd,
    paymentPlan: req.paymentPlan,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  milestones.forEach((m) => {
    const mRef = doc(collection(projectRef, "milestones"));
    batch.set(mRef, {
      label: m.label,
      pct: m.pct,
      amount: m.amount,
      dueAt: null,
      status: "pending",
      paidAt: null,
      order: m.order,
    });
  });

  batch.update(reqRef, {
    status: "accepted",
    projectId: projectRef.id,
    updatedAt: serverTimestamp(),
  });

  await batch.commit();

  // Tell the account their project is a go (fire-and-forget).
  api
    .post("/project-event", {
      accountId: req.accountId,
      event: "request_accepted",
      projectTitle: title || "Your project",
      detail: req.type,
    })
    .catch(() => {});
  return projectRef.id;
};
